import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import { markRaw } from "vue";
import { analysisRestClient } from "@/api";
import db from "@/db";
import { isNetworkError } from "@/utils/network";

const Analysis = db.models.Analysis;

export const useAnalysisStore = defineStore("analysis", {
  state: () => ({
    filteredAnalysis: [],
    analysisIsLoading: false,
  }),
  actions: {
    async fetchAnalysis(filters = {}) {
      if (isNetworkUnavailable()) return;

      this.analysisIsLoading = true;
      try {
        const { results } = await analysisRestClient.list(filters);
        await db.transaction("rw", db.analysis, async () => {
          for (const analysisResponse of results) {
            let indicativeUncertainty = "";
            if (analysisResponse.indicativeUncertainty !== "") {
              if (
                Number.isNaN(
                  Number.parseFloat(analysisResponse.indicativeUncertainty)
                )
              )
                indicativeUncertainty = analysisResponse.indicativeUncertainty;
              else
                indicativeUncertainty = (
                  Number.parseFloat(
                    analysisResponse.indicativeUncertainty.replace(",", ".")
                  ) * 100
                ).toFixed();
            }
            Analysis.createOrUpdate({
              ...analysisResponse,
              indicativeUncertainty,
            });
          }
        });
        return { success: true, error: null };
      } catch (error) {
        if (!isNetworkError(error)) throw error;
        return { success: false, error };
      } finally {
        this.analysisIsLoading = false;
      }
    },

    async setAllAnalysis() {
      const analysisList = await db.analysis.toArray();
      for (const analysis of analysisList) {
        await analysis.setRelationships();
      }
      this.filteredAnalysis = analysisList.map(markRaw);
    },

    async getParametersAnalyzes(parametersIds) {
      const queryset = await db.analysis.filter((item) =>
        item.parameters.some((paramId) => parametersIds.includes(paramId))
      );

      return queryset.toArray();
    },

    async filterAnalysis({ code, name }) {
      this.analysisIsLoading = true;

      const analysisList = await db.analysis
        .filter(
          (item) =>
            item.code.toLowerCase().includes(code.toLowerCase()) &&
            item.name.toLowerCase().includes(name.toLowerCase())
        )
        .toArray();
      for (const analysis of analysisList) {
        await analysis.setRelationships();
      }
      this.filteredAnalysis = analysisList.map(markRaw);
      this.analysisIsLoading = false;
    },

    async updateAnalysis(analysis) {
      const response = await analysisRestClient.update(analysis);
      const dexieUuid = await Analysis.createOrUpdate(response);

      const updatedAnalysis = await db.analysis.get({ uuid: dexieUuid });
      await updatedAnalysis.setRelationships();

      const index = this.filteredAnalysis.findIndex(
        ({ uuid }) => updatedAnalysis.uuid === uuid
      );
      if (index >= 0) {
        this.filteredAnalysis[index] = markRaw(updatedAnalysis);
        this.filteredAnalysis = [...this.filteredAnalysis];
      }
      return updatedAnalysis;
    },

    async updateReference(reference) {
      await analysisRestClient.updateReference(reference);
      await db.analysis.toCollection().modify({ catalogReference: reference });
    },
  },
});
